import {
    asArraySafe,
    asNumberSafe,
    joinStrings,
    mapToSelectOptions,
    normalizeString,
} from '@pf/shared-utility';
import { inject, Injectable } from '@angular/core';
import {
    ExportConfig,
    IPagedResult,
    IQueryParams,
    ISearchFacade,
    ITypeEntityFragment,
    SelectOption,
} from '@pf/shared-common';
import {
    BehaviorSubject,
    combineLatest,
    map,
    Observable,
    of,
    switchMap,
    tap,
} from 'rxjs';
import { mapToIPagedResult } from '@pf/shared/util-platform';
import { ContainerDto } from '../entities/container.dto';
import { ContainerFacade } from './container.facade';
import {
    EquipmentStatus,
    InventoryBundleFragment,
    WarehouseFragment,
} from '@control-tower/platform-wms';
import { IWarehouseEntity } from '../interfaces/warehouse-entity';
import { EquipmentTypeFacade } from './equipment-type.facade';
import { EquipmentTypeDto } from '../entities/equipment-type.dto';
import { EquipmentFacade } from './equipment.facade';
import {
    EquipmentDto,
    EquipmentStatusOptions,
} from '../entities/equipment.dto';
import { InventoryBundleStatusOptions } from '../entities/inventory-bundle.dto';
import { startCase, sumBy } from 'lodash-es';

export class HandlingUnit implements IWarehouseEntity {
    id!: string;
    name!: string;
    isEquipmentType!: boolean;
    isContainer!: boolean;
    isEquipment!: boolean;
    isInventoryBundle!: boolean;
    lpnPrefix!: string;
    status!: string;
    equipment?: EquipmentDto;
    containerType?: ITypeEntityFragment;
    equipmentType?: EquipmentTypeDto;
    parentEquipmentId?: string | null;
    parentContainerId?: string | null;
    totalQuantity?: number;
    children?: HandlingUnit[];
    warehouse!: WarehouseFragment;

    constructor(data?: Partial<HandlingUnit>) {
        this.children = [];
        Object.assign(this, data);
    }

    get typeLabel() {
        if (this.isContainer && this.containerType) {
            return this.containerType.name;
        }
        if (this.isEquipment && this.equipmentType) {
            return this.equipmentType.name;
        }
        return '';
    }

    get assignedTo() {
        if (this.isEquipment) {
            const user = this.equipment?.assignedToUser;
            return user
                ? joinStrings(' ', user.firstName, user.lastName)
                : 'Unassigned';
        }
        return '';
    }

    get statusLabel() {
        if (this.isEquipment) {
            const zoneType = this.equipment?.zone?.zoneType;
            if (zoneType) {
                return `${this.status} for ${startCase(zoneType)}`;
            }

            // Compute from bundle statuses when no zoneType
            const bundleStatuses = this.getBundleStatuses();
            if (bundleStatuses.length > 0) {
                const allPicked = bundleStatuses.every(s => s === 'Picked');
                const allReceived = bundleStatuses.every(s => s === 'Received');

                if (allPicked) {
                    return `${this.status} for Packing`;
                } else if (allReceived) {
                    return `${this.status} for Putaway`;
                } else {
                    return `${this.status} for Putaway/Packing`;
                }
            }

            return this.status;
        }

        return this.status;
    }

    private getBundleStatuses(): string[] {
        const statuses: string[] = [];
        for (const child of asArraySafe(this.children)) {
            if (child.isContainer) {
                for (const bundle of asArraySafe(child.children)) {
                    if (bundle.isInventoryBundle && bundle.status) {
                        statuses.push(bundle.status);
                    }
                }
            }
        }
        return statuses;
    }

    get statusColor() {
        if (this.isContainer) {
            return (
                EquipmentStatusOptions[this.status]?.color ||
                EquipmentStatusOptions.defaultOption.color
            );
        }
        if (this.isEquipment) {
            return (
                EquipmentStatusOptions[this.status]?.color ||
                EquipmentStatusOptions.defaultOption.color
            );
        }
        if (this.isInventoryBundle) {
            return (
                InventoryBundleStatusOptions[this.status]?.color ||
                InventoryBundleStatusOptions.defaultOption.color
            );
        }
        return '';
    }

    get count(): number {
        // For containers, sum the totalQuantity of inventory bundles
        if (this.isContainer) {
            return sumBy(asArraySafe(this.children), 'totalQuantity');
        }

        // For equipment, count only immediate children
        if (this.isEquipmentType || this.isEquipment) {
            return asNumberSafe(asArraySafe(this.children).length);
        }

        return 0;
    }
}

@Injectable()
export class HandlingUnitsFacade implements ISearchFacade<HandlingUnit> {
    private _containerFacade = inject(ContainerFacade);
    private _equipmentFacade = inject(EquipmentFacade);
    private _equipmentTypeFacade = inject(EquipmentTypeFacade);

    currentPage$ = new BehaviorSubject<IPagedResult<HandlingUnit>>({
        data: [],
        totalRecords: 0,
        currentPageSize: 0,
        currentPage: 1,
    });
    private lastQueryParams?: IQueryParams<HandlingUnit>;
    parentIdField: keyof HandlingUnit = 'parentEquipmentId';

    getEntityName(entity: HandlingUnit): string {
        return entity.name;
    }

    reload$(): Observable<boolean> {
        return this.search$(this.lastQueryParams).pipe(map(() => true));
    }

    reloadAll$(): Observable<boolean> {
        return this.searchAll$(this.lastQueryParams).pipe(map(() => true));
    }

    search$(
        params?: IQueryParams<HandlingUnit>
    ): Observable<IPagedResult<HandlingUnit>> {
        this.lastQueryParams = params;
        return this.getHandlingUnits$(params).pipe(
            tap(result => {
                this.currentPage$.next(result);
            })
        );
    }

    oneTimeSearch$(
        params: IQueryParams<HandlingUnit>
    ): Observable<IPagedResult<HandlingUnit>> {
        return this.getHandlingUnits$(params);
    }

    searchAll$(
        params?: IQueryParams<HandlingUnit>,
        addToPage?: boolean
    ): Observable<HandlingUnit[]> {
        this.lastQueryParams = params;
        return this.getHandlingUnits$(params).pipe(
            tap(result => {
                if (addToPage) {
                    this.currentPage$.next(result);
                }
            }),
            map(result => result.data)
        );
    }

    export$(
        _: ExportConfig<HandlingUnit, HandlingUnit>
    ): Observable<boolean | 'Cancelled'> {
        // Not needed
        throw new Error('Method not implemented.');
    }

    getById(id: string): HandlingUnit | null {
        const equipmentType = this._equipmentTypeFacade.getById(id);
        if (equipmentType) {
            return this.mapEquipmentTypeToHandlingUnit(equipmentType);
        }

        const equipment = this._equipmentFacade.getById(id);
        if (equipment) {
            return this.mapEquipmentToHandlingUnit(equipment);
        }
        const container = this._containerFacade.getById(id);

        if (container) {
            return this.mapContainerToHandlingUnit(container);
        }
        return null;
    }

    getById$(id: string): Observable<HandlingUnit | null> {
        return of(this.getById(id));
    }

    getMany$(ids: string[]): Observable<HandlingUnit[]> {
        return combineLatest(
            ids.map(id => this.getById$(id)).filter(Boolean)
        ) as Observable<HandlingUnit[]>;
    }

    searchByText$(
        text: string,
        pageSize = 25,
        defaultFilters: Partial<
            Record<'search' | keyof HandlingUnit, string | string[]>
        > = {}
    ): Observable<IPagedResult<HandlingUnit>> {
        const params: IQueryParams<HandlingUnit> = {
            pageIndex: 1,
            pageSize: pageSize,
            sortOrder: 'ascend',
            sortField: 'lpn',
            filters: { name: text },
        };
        return this.oneTimeSearch$({
            ...params,
            filters: {
                ...params.filters,
                ...(defaultFilters || {}),
            },
        });
    }

    mapToSelectOptions(
        source: Observable<IPagedResult<HandlingUnit>>
    ): Observable<SelectOption[]> {
        return mapToSelectOptions<HandlingUnit>('name', 'id')(source);
    }

    getHandlingUnits$(
        params?: IQueryParams<HandlingUnit>
    ): Observable<IPagedResult<HandlingUnit>> {
        return this._equipmentTypeFacade.searchAll$(params).pipe(
            map(equipmentTypes =>
                equipmentTypes.map(type =>
                    this.mapEquipmentTypeToHandlingUnit(type)
                )
            ),
            switchMap(rootHandlingUnits => {
                if (rootHandlingUnits.length === 0) {
                    return of([] as HandlingUnit[]);
                }
                return combineLatest(
                    rootHandlingUnits.map(unit =>
                        this.getEquipmentByEquipmentType$(
                            unit.equipmentType!
                        ).pipe(
                            map(equipmentHandlingUnits => {
                                unit.children = equipmentHandlingUnits;
                                return unit;
                            })
                        )
                    )
                );
            }),
            map(units =>
                units
                    .filter(unit => handlingUnitsFilter(unit, params))
                    .sort(handlingUnitsSorter)
            ),
            mapToIPagedResult,
            tap((result: IPagedResult<HandlingUnit>) => {
                this.currentPage$.next(result);
            })
        );
    }

    private getEquipmentByEquipmentType$(
        equipmentType: EquipmentTypeDto
    ): Observable<HandlingUnit[]> {
        return this._equipmentFacade
            .searchAll$({
                pageIndex: 1,
                pageSize: 50,
                filters: { equipmentType: equipmentType.id },
            })
            .pipe(
                map(equipment =>
                    equipment.map(e => this.mapEquipmentToHandlingUnit(e))
                )
            );
    }

    private mapEquipmentTypeToHandlingUnit(equipmentType: EquipmentTypeDto) {
        return new HandlingUnit({
            id: equipmentType.id,
            equipmentType: equipmentType,
            name: equipmentType.name,
            status: '',
            isContainer: false,
            isEquipmentType: true,
            isEquipment: false,
            isInventoryBundle: false,
            lpnPrefix: equipmentType.code,
            parentEquipmentId: null,
            children: [],
        });
    }

    private mapEquipmentToHandlingUnit(equipment: EquipmentDto): HandlingUnit {
        const equipmentType = this._equipmentTypeFacade.getById(
            equipment.equipmentType.id as string
        ) as EquipmentTypeDto;
        return new HandlingUnit({
            id: equipment.id,
            equipmentType,
            equipment,
            name: equipment.name || '',
            status: equipment.status,
            isContainer: false,
            isEquipmentType: false,
            isEquipment: true,
            isInventoryBundle: false,
            lpnPrefix: equipmentType.code || '',
            parentEquipmentId: equipmentType.id,
            children: asArraySafe(equipment.containers).map(c =>
                this.mapContainerToHandlingUnit(c)
            ),
        });
    }

    private mapContainerToHandlingUnit(container: ContainerDto): HandlingUnit {
        if (!container.equipment) {
            throw new Error('Container must have an equipment');
        }
        const equipment = this._equipmentFacade.getById(
            container.equipment.id as string
        ) as EquipmentDto;

        if (!equipment?.equipmentType) {
            throw new Error('Equipment type not found');
        }

        const equipmentType = this._equipmentTypeFacade.getById(
            equipment.equipmentType.id as string
        ) as EquipmentTypeDto;

        const bundleChildren = asArraySafe(container.inventoryBundles)
            .filter(bundle => {
                return !!bundle.totalQuantity;
            })
            .map(bundle =>
                this.mapInventoryBundleToHandlingUnit(bundle, container.id)
            );

        return new HandlingUnit({
            id: container.id,
            equipmentType,
            name: container.lpn || '',
            status:
                bundleChildren.length > 0
                    ? EquipmentStatus.InUse
                    : EquipmentStatus.Available,
            isContainer: true,
            isInventoryBundle: false,
            containerType: container.containerType as ITypeEntityFragment,
            lpnPrefix: equipmentType.code || '',
            parentEquipmentId: equipment.id,
            children: bundleChildren,
        });
    }

    private mapInventoryBundleToHandlingUnit(
        bundle: InventoryBundleFragment,
        parentContainerId: string
    ): HandlingUnit {
        const name =
            bundle.item?.name && (bundle.item?.code || bundle.item?.sku)
                ? `${bundle.item.name} (${bundle.item.code || bundle.item?.sku})`
                : bundle.item?.name || bundle.item?.code || '';
        const totalQuantity = asNumberSafe(bundle.totalQuantity);
        return new HandlingUnit({
            id: bundle.id || '',
            name: `${totalQuantity} of ${name}`,
            status: bundle.status || '',
            isContainer: false,
            isEquipmentType: false,
            isEquipment: false,
            isInventoryBundle: true,
            lpnPrefix: '',
            parentContainerId: parentContainerId,
            totalQuantity: totalQuantity,
            children: [],
        });
    }
}

function handlingUnitsSorter(prev: HandlingUnit, next: HandlingUnit) {
    return normalizeString(prev.name).localeCompare(normalizeString(next.name));
}

function handlingUnitsFilter(
    handlingUnit: HandlingUnit,
    params?: IQueryParams<HandlingUnit>
) {
    const searchFilter = normalizeString(params?.searchValue || '');
    const hasChildren = !!handlingUnit.children?.length;
    const matchesEquipmentType =
        !params?.filters?.equipmentType ||
        normalizeString(handlingUnit.equipmentType?.name) ===
            normalizeString(params?.filters.equipmentType as string);
    const matchesSearch =
        matchesEquipmentType &&
        (!searchFilter ||
            normalizeString(handlingUnit.name).indexOf(searchFilter) > -1);
    return hasChildren || matchesSearch;
}
